import { writable, type Writable } from "svelte/store";
import type { Locale, UserRole } from "@diy-avatar/shared/api";

export interface SessionUser {
  email: string;
  /** Nickname — shown instead of the email across the UI. */
  display_name: string | null;
  /** Full/real name (distinct from the nickname). */
  full_name?: string | null;
  roles: UserRole[];
  /** Preferred UI language, learned at login. Defaults to "de". */
  language: Locale;
  /** True when this is the guarded demo principal (Test-Modus). */
  demo?: boolean;
  /**
   * True when the backend requires the user to change their password before
   * using the app (admin created the account with an initial password). The
   * Class view forces a Change Password screen while this is set.
   */
  must_change_password?: boolean;
}

/** Shallow-merge a partial update into the current session user (no-op if none). */
export function patchUser(patch: Partial<SessionUser>): void {
  session.update((s) =>
    s.user ? { ...s, user: { ...s.user, ...patch } } : s,
  );
}

export interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  error: string | null;
}

const initial: SessionState = {
  user: null,
  loading: false,
  error: null,
};

export const session: Writable<SessionState> = writable(initial);

export function setUser(user: SessionUser | null): void {
  session.update((s) => ({ ...s, user, error: null }));
}

export function setLoading(loading: boolean): void {
  session.update((s) => ({ ...s, loading }));
}

export function setError(error: string | null): void {
  session.update((s) => ({ ...s, error }));
}

/**
 * Flip the current user's `must_change_password` flag (e.g. clear it after a
 * successful change so the forced-change guard releases the app). No-op when
 * there is no logged-in user.
 */
export function setMustChangePassword(value: boolean): void {
  session.update((s) =>
    s.user ? { ...s, user: { ...s.user, must_change_password: value } } : s,
  );
}

export function reset(): void {
  session.set(initial);
}
